<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import AppSelectField from '@/components/AppSelectField.vue'
import { MATERIAL_SEARCH_TYPE_OPTIONS } from '@/components/appSelectOptions'
import { NPagination, NTree, useMessage } from 'naive-ui'
import type { TreeOption } from 'naive-ui'
import { useAuthStore } from '@/features/auth'
import { MATERIAL_EMBED_PAGES, MATERIAL_MALL_RULE_URL } from '@/features/material-library/config'
import MaterialPreviewPane from '@/material/web/MaterialPreviewPane.vue'
import MaterialPreviewAssets, { type MaterialPreviewOverrideItem } from '@/material/web/MaterialPreviewAssets.vue'
import { useMaterialLibraryCatalog } from '@/material/web/useMaterialLibraryCatalog'
import { Category } from '@/material/web/category'
import type { MaterialGoods } from '@/material/web/api'
import { resolveMaterialCardMode, type MaterialCardMode } from '@/material/web/previewUtils'
import { applyOuiqTemplateToProject } from '@/features/ui-template/apply'
import { listBuiltinUiTemplates, loadBuiltinUiTemplateBytes } from '@/features/ui-template/builtinCatalog'
import {
  applyOfficialFontToResourceRoot,
  collectOfficialFontCategoryTree,
  loadOfficialFontLibrary,
  type OfficialFontItem,
} from '@/features/project-font/library'
import type MainProject from '@/model/MainProject'
import { useWorkspaceStore } from '@/store/workspace'
import { blurModalSearchFocus } from '@/utils/blurModalSearchFocus'

const props = defineProps<{
  show: boolean
}>()

const emit = defineEmits<{
  'update:show': [boolean]
}>()

const message = useMessage()
const auth = useAuthStore()
const workspace = useWorkspaceStore()
const localUiActive = ref(false)
const localFontActive = ref(false)
const localUiPage = ref(1)
const applyingLocalTemplateId = ref('')
const applyingFontKey = ref('')
const selectedLocalUiTemplateId = ref('')
const selectedFontCategoryKey = ref('')
const selectedFontKey = ref('')
const fontExpandedTreeKeys = ref<Array<string | number>>([])
const pendingExpandTreeKey = ref<string | number | null>(null)
const officialFonts = ref<OfficialFontItem[]>([])
const fontLoadError = ref('')
const searchDraft = ref('')
const goodsListStageRef = ref<HTMLElement | null>(null)
let goodsListResizeObserver: ResizeObserver | null = null
const LOCAL_UI_PAGE_SIZE = 6
const GOODS_GRID_GAP = 7
const GOODS_GRID_PADDING_WIDTH = 12
const GOODS_GRID_PADDING_HEIGHT = 12
const GOODS_CARD_SIZE = {
  vertical: { width: 110, height: 166 },
  horizontal: { width: 192, height: 106 },
  music: { width: 192, height: 32 },
} as const
const materialAuth = computed(() =>
  auth.loggedIn && auth.uid > 0 && auth.sessionId
    ? {
        uid: auth.uid,
        sessionId: auth.sessionId,
      }
    : null,
)
const authReady = computed(() => !!materialAuth.value)
const currentProject = computed(() => workspace.project as MainProject | null)
const localUiTemplates = computed(() => {
  const project = currentProject.value
  if (!project) return []
  return listBuiltinUiTemplates({
    width: Math.max(1, Number(project.screenHash?.width ?? project.Width ?? 1280)),
    height: Math.max(1, Number(project.screenHash?.height ?? project.Height ?? 720)),
  })
})
const selectedLocalUiTemplate = computed(() =>
  localUiTemplates.value.find((item) => item.id === selectedLocalUiTemplateId.value)
  ?? localUiTemplates.value[0]
  ?? null,
)
const pagedLocalUiTemplates = computed(() => {
  const start = (localUiPage.value - 1) * LOCAL_UI_PAGE_SIZE
  return localUiTemplates.value.slice(start, start + LOCAL_UI_PAGE_SIZE)
})
const selectedLocalUiGoods = computed(() => {
  const item = selectedLocalUiTemplate.value
  if (!item) return null
  return {
    id: item.id,
    name: item.label,
    intro: item.sourceOuiName,
    price: '',
    extra: `${item.width} × ${item.height}`,
    hasDoll: '',
    cid: Category.UI,
    thumbUrl: item.previewUrl,
  }
})
const selectedLocalUiPreviewItems = computed<MaterialPreviewOverrideItem[]>(() => {
  const item = selectedLocalUiTemplate.value
  if (!item) return []
  const urls = item.galleryUrls.filter(Boolean)
  if (!urls.length && item.previewUrl) {
    urls.push(item.previewUrl)
  }
  return [...new Set(urls)].map((url, index) => ({
    id: `${item.id}:${index}`,
    url,
    extra: item.sourceOuiName,
  }))
})
const selectedLocalUiSizeOptions = computed(() => {
  const item = selectedLocalUiTemplate.value
  if (!item) return []
  return [{
    label: `${item.width}×${item.height}`,
    value: `${item.width}x${item.height}`,
  }]
})
const displayedTreeData = computed(() => {
  if (localFontActive.value) return collectOfficialFontCategoryTree(officialFonts.value)
  if (favoritesOnly.value || selectedTopId.value !== Category.UI) return treeData.value
  return [
    {
      key: 'local-ui',
      label: `本地（${localUiTemplates.value.length}）`,
      isLeaf: true,
    },
    ...treeData.value,
  ]
})
const displayedSelectedTreeKeys = computed(() => {
  if (localFontActive.value) return selectedFontKey.value ? [`font:${selectedFontKey.value}`] : []
  return localUiActive.value ? ['local-ui'] : selectedTreeKeys.value
})
function collectFontExpandedTreeKeys(items: OfficialFontItem[]): Array<string | number> {
  return [...new Set(items.flatMap((item) => {
    const segments = item.categoryPath.split('/').filter(Boolean)
    const top = segments[0] || '默认'
    const keys = [`category:${top}`]
    const child = segments.slice(1).join('/')
    if (child) keys.push(`category:${top}/${child}`)
    return keys
  }))]
}
const showGoodsLoading = computed(() => !loadingInit.value && loadingList.value)
const showPreviewLoading = computed(() => !loadingInit.value && previewLoading.value)
const showCategorySidebar = computed(() => localUiActive.value || localFontActive.value || !hasSearch.value)
const displayedExpandedTreeKeys = computed(() => localFontActive.value ? fontExpandedTreeKeys.value : expandedTreeKeys.value)
const selectedOfficialFont = computed(() =>
  officialFonts.value.find((item) => item.key === selectedFontKey.value)
  ?? null,
)

function blurActiveElement() {
  const active = document.activeElement
  if (active instanceof HTMLElement) {
    active.blur()
  }
}

const {
  pageSize,
  loadingInit,
  loadingList,
  previewLoading,
  topCategories,
  treeData,
  expandedTreeKeys,
  selectedTreeKeys,
  goodsList,
  currentGoods,
  currentResources,
  favoritesOnly,
  selectedTopId,
  page,
  total,
  searchText,
  searchType,
  hasSearch,
  topCategoryMode,
  isRecommendTopCategory,
  currentGoodsTopCategoryId,
  resolveGoodsTopCategoryId,
  currentCollected,
  initialize,
  activateTopCategory,
  handleTreeLoad: onTreeLoad,
  handleTreeSelect,
  runSearch,
  clearSearch,
  changePage,
  selectGoods,
  updatePageSize,
  setFavoritesOnly,
  toggleCurrentCollection,
} = useMaterialLibraryCatalog(materialAuth, message)

function resolveGoodsCardMode(goods: MaterialGoods): MaterialCardMode {
  if (isRecommendTopCategory.value) {
    return resolveMaterialCardMode(resolveGoodsTopCategoryId(goods))
  }
  return topCategoryMode.value
}

function calculateGoodsPageSize(width: number, height: number): number {
  const mode = isRecommendTopCategory.value ? 'vertical' : topCategoryMode.value
  const size = GOODS_CARD_SIZE[mode]
  const columns = Math.floor((width - GOODS_GRID_PADDING_WIDTH + GOODS_GRID_GAP) / (size.width + GOODS_GRID_GAP))
  const rows = Math.floor((height - GOODS_GRID_PADDING_HEIGHT + GOODS_GRID_GAP) / (size.height + GOODS_GRID_GAP))
  const viewport = window.visualViewport
  const viewportWidth = Math.round(viewport?.width ?? window.innerWidth)
  const viewportHeight = Math.round(viewport?.height ?? window.innerHeight)
  const minPageSize = viewportWidth <= 900 && viewportWidth < viewportHeight ? 4 : 1
  return Math.max(minPageSize, Math.max(1, columns) * Math.max(1, rows))
}

function updateGoodsPageSizeFromLayout() {
  if (localUiActive.value || localFontActive.value) return
  const el = goodsListStageRef.value
  if (!el) return
  if (el.offsetWidth <= 0 || el.offsetHeight <= 0) return
  void updatePageSize(calculateGoodsPageSize(el.offsetWidth, el.offsetHeight))
}

function syncGoodsListResizeObserver() {
  if (!goodsListResizeObserver) return
  goodsListResizeObserver.disconnect()
  if (goodsListStageRef.value) {
    goodsListResizeObserver.observe(goodsListStageRef.value)
  }
}

function close() {
  blurActiveElement()
  emit('update:show', false)
}

function openExternal(url: string) {
  if (!url) return
  window.open(url, '_blank', 'noopener,noreferrer')
}

function handleModalShowChange(value: boolean) {
  if (!value) {
    blurActiveElement()
  }
  emit('update:show', value)
}

function handleTopCategoryClick(topId: string) {
  localUiActive.value = false
  localFontActive.value = false
  void activateTopCategory(topId)
}

function handleFavoritesToggle() {
  localUiActive.value = false
  localFontActive.value = false
  void setFavoritesOnly(!favoritesOnly.value)
}

function activateLocalUi() {
  localUiActive.value = true
  localFontActive.value = false
  localUiPage.value = 1
  selectedLocalUiTemplateId.value = localUiTemplates.value[0]?.id ?? ''
}

async function activateLocalFont() {
  localUiActive.value = false
  localFontActive.value = true
  try {
    fontLoadError.value = ''
    const library = await loadOfficialFontLibrary()
    officialFonts.value = library.items
    fontExpandedTreeKeys.value = collectFontExpandedTreeKeys(officialFonts.value)
    selectedFontCategoryKey.value = officialFonts.value[0]?.categoryPath.split('/')[0] ?? ''
    selectedFontKey.value = officialFonts.value[0]?.key ?? ''
  } catch (error) {
    fontLoadError.value = error instanceof Error ? error.message : '官方字体库读取失败'
    officialFonts.value = []
  }
}

function handleLocalUiTemplateClick(templateId: string) {
  selectedLocalUiTemplateId.value = templateId
}

async function handleApplyLocalUi(templateId: string) {
  const project = currentProject.value
  if (!project) {
    message.warning('当前没有已打开工程')
    return
  }
  applyingLocalTemplateId.value = templateId
  try {
    const bytes = await loadBuiltinUiTemplateBytes({
      templateId,
      dataVersion: project.projectConstant.dataVersion || project.dataVer,
    })
    const applied = applyOuiqTemplateToProject({
      project,
      roots: workspace.virtualResourceRoots,
      source: bytes,
    })
    workspace.replaceVirtualResourceRoots(applied.roots)
    await workspace.uploadVirtualResourcesToCloud()
    workspace.revealResourceBrowserFolder('graphic', ['UI'])
    message.success('本地 UI 已应用')
  } catch (error) {
    message.error(error instanceof Error ? error.message : '应用本地 UI 失败')
  } finally {
    applyingLocalTemplateId.value = ''
  }
}

function hasAppliedFont(item: OfficialFontItem): boolean {
  const root = workspace.virtualResourceRoots.font
  if (!root) return false
  const stack = [root]
  while (stack.length) {
    const node = stack.pop()!
    if (node.isFolder) {
      stack.push(...node.children)
      continue
    }
    if (node.links[1] === item.key || node.resourceMd5 === item.md5 || node.relativePath === item.path.replace(/^font\//i, '')) {
      return true
    }
  }
  return false
}

async function handleApplyOfficialFont(item: OfficialFontItem) {
  applyingFontKey.value = item.key
  try {
    const mapped = applyOfficialFontToResourceRoot({
      root: workspace.virtualResourceRoots.font,
      item,
    })
    workspace.setVirtualResourceRoot('font', mapped.root)
    await workspace.uploadVirtualResourcesToCloud()
    workspace.revealResourceBrowserFolder('font', mapped.folderSegments)
    message.success('字体已添加到资源管理器')
  } catch (error) {
    message.error(error instanceof Error ? error.message : '应用字体失败')
  } finally {
    applyingFontKey.value = ''
  }
}

function handleTreeSelectedUpdate(keys: Array<string | number>) {
  const key = String(keys[0] ?? '')
  if (localFontActive.value) {
    if (key.startsWith('font:')) {
      selectedFontKey.value = key.slice('font:'.length)
      selectedFontCategoryKey.value = selectedOfficialFont.value?.categoryPath ?? ''
      return
    }
    if (key.startsWith('category:')) {
      const categoryPath = key.slice('category:'.length)
      selectedFontCategoryKey.value = categoryPath
      selectedFontKey.value = officialFonts.value.find((item) =>
        item.categoryPath === categoryPath || item.categoryPath.startsWith(`${categoryPath}/`)
      )?.key ?? ''
    }
    return
  }
  if (key === 'local-ui') {
    activateLocalUi()
    return
  }
  localUiActive.value = false
  localFontActive.value = false
  void handleTreeSelect(keys)
}

function handleTreeExpandedUpdate(keys: Array<string | number>): Array<string | number> {
  const previousKeys = expandedTreeKeys.value
  const forcedKey = pendingExpandTreeKey.value
  if (forcedKey === null) {
    expandedTreeKeys.value = keys
    return keys.filter((key) => !previousKeys.includes(key))
  }
  expandedTreeKeys.value = [...new Set([...keys, forcedKey])]
  pendingExpandTreeKey.value = null
  return []
}

function handleDisplayedTreeExpandedUpdate(keys: Array<string | number>) {
  if (localFontActive.value) {
    fontExpandedTreeKeys.value = keys
    return
  }
  const expandedKeys = handleTreeExpandedUpdate(keys)
  const nextKey = expandedKeys[0]
  if (nextKey === undefined || nextKey === null) return
  void handleTreeSelect([nextKey])
}

function getMaterialTreeNodeProps(option: TreeOption) {
  const forceExpandNode = () => {
    const key = option.key
    if (key === undefined || key === null || key === 'local-ui' || localFontActive.value) return
    if (option.isLeaf) return
    pendingExpandTreeKey.value = key
    expandedTreeKeys.value = [...new Set([...expandedTreeKeys.value, key])]
  }
  return {
    onMousedown: forceExpandNode,
    onClick: forceExpandNode,
  }
}

function getDisplayedTreeNodeProps(option: TreeOption) {
  return localFontActive.value ? {} : getMaterialTreeNodeProps(option)
}

function handleGoodsCardClick(item: (typeof goodsList.value)[number]) {
  void selectGoods(item)
}

function handlePageUpdate(nextPage: number) {
  void changePage(nextPage)
}

async function submitSearch() {
  searchText.value = searchDraft.value
  await runSearch()
}

async function resetSearch() {
  searchDraft.value = ''
  await clearSearch()
}

function syncSearchDraftFromCatalog() {
  searchDraft.value = searchText.value
}

function handleLocalUiPageUpdate(nextPage: number) {
  localUiPage.value = nextPage
  selectedLocalUiTemplateId.value = pagedLocalUiTemplates.value[0]?.id ?? selectedLocalUiTemplateId.value
}

watch(
  () => props.show,
  (show) => {
    if (show) {
      syncSearchDraftFromCatalog()
      void initialize()
      void nextTick(() => blurModalSearchFocus('.material-library-overlay'))
    }
  },
  { immediate: true },
)

watch(
  () => auth.sessionId,
  () => {
    if (!props.show || !authReady.value) return
    void initialize(true)
  },
)

watch(searchText, syncSearchDraftFromCatalog)

watch(
  [() => props.show, loadingInit, loadingList, topCategoryMode, localUiActive, localFontActive, hasSearch],
  async () => {
    await nextTick()
    syncGoodsListResizeObserver()
    updateGoodsPageSizeFromLayout()
  },
  { immediate: true, flush: 'post' },
)

onMounted(() => {
  goodsListResizeObserver = new ResizeObserver(() => updateGoodsPageSizeFromLayout())
  void nextTick(() => {
    syncGoodsListResizeObserver()
    updateGoodsPageSizeFromLayout()
  })
})

onBeforeUnmount(() => {
  goodsListResizeObserver?.disconnect()
  goodsListResizeObserver = null
})
</script>

<template>
  <Teleport to="body">
    <div v-if="show" class="material-library-overlay" @mousedown.self="handleModalShowChange(false)">
      <section class="material-modal">
      <header class="material-header">
        <div class="material-title">素材库</div>
        <div class="material-header-actions">
          <button type="button" class="material-tool-button" @click="openExternal(MATERIAL_EMBED_PAGES[0]?.url || '')">橙娘素材铺</button>
          <button type="button" class="material-tool-button" @click="openExternal(MATERIAL_MALL_RULE_URL)">使用协议</button>
          <button type="button" class="material-tool-button" @click="close">关闭</button>
        </div>
      </header>

      <div v-if="!authReady" class="login-empty">
        请先在主界面登录橙光账号，再使用素材库接口
      </div>

      <template v-else>
        <div class="material-top-tabs">
          <button
            v-for="item in topCategories"
            :key="item.id"
            type="button"
            class="top-tab"
            :class="{ 'top-tab-active': !localFontActive && !favoritesOnly && selectedTopId === item.id }"
            @click="handleTopCategoryClick(item.id)"
          >
            {{ item.name }}
          </button>
          <button
            type="button"
            class="top-tab"
            :class="{ 'top-tab-active': localFontActive }"
            @click="activateLocalFont"
          >
            字体
          </button>
          <button
            type="button"
            class="top-tab top-tab-favorite"
            :class="{ 'top-tab-active': !localUiActive && !localFontActive && favoritesOnly }"
            @click="handleFavoritesToggle"
          >
            收藏
          </button>
        </div>

        <form v-if="!localUiActive && !localFontActive" class="material-searchbar" @submit.prevent.stop="submitSearch">
          <AppSelectField
            v-model="searchType"
            :use-cell-style="false"
            input-class="material-searchbar__type"
            :options="MATERIAL_SEARCH_TYPE_OPTIONS"
          />
          <input
            v-model="searchDraft"
            type="text"
            class="material-searchbar__input"
            autocomplete="off"
            spellcheck="false"
            placeholder="搜索素材名称或画师"
          />
          <button type="submit" class="material-searchbar__button material-searchbar__button--primary">搜索</button>
          <button type="button" class="material-searchbar__button" @click="resetSearch">清空</button>
        </form>

        <div v-if="loadingInit" class="material-loading">
          正在加载素材库...
        </div>

        <div v-else class="material-body" :class="{ 'material-body--search': !localUiActive && !localFontActive && hasSearch }">
          <aside v-if="showCategorySidebar" class="sidebar">
            <n-tree
              block-line
              selectable
              expand-on-click
              :animated="false"
              :data="displayedTreeData"
              :expanded-keys="displayedExpandedTreeKeys"
              :selected-keys="displayedSelectedTreeKeys"
              :on-load="localFontActive ? undefined : onTreeLoad"
              :node-props="getDisplayedTreeNodeProps"
              class="material-category-tree"
              @update:expanded-keys="handleDisplayedTreeExpandedUpdate"
              @update:selected-keys="handleTreeSelectedUpdate"
            />
          </aside>

          <section v-if="localUiActive" class="goods-panel">
            <div class="goods-header">
              <div class="goods-summary">本地 UI：{{ localUiTemplates.length }} 项</div>
              <div class="goods-mode">皮肤</div>
            </div>
            <div v-if="localUiTemplates.length" class="goods-grid goods-grid-horizontal">
              <button
                v-for="item in pagedLocalUiTemplates"
                :key="item.id"
                type="button"
                class="goods-card"
                :class="{ 'goods-card-active': selectedLocalUiTemplate?.id === item.id }"
                @click="handleLocalUiTemplateClick(item.id)"
              >
                <img :src="item.previewUrl" :alt="item.label" class="goods-thumb" />
                <div class="goods-name">{{ item.label }}</div>
                <div class="goods-author">{{ item.width }} × {{ item.height }}</div>
              </button>
            </div>
            <div v-else class="goods-empty">当前工程分辨率没有可用的本地 UI 皮肤</div>
            <div class="pager-wrap">
              <n-pagination
                :page="localUiPage"
                size="small"
                :page-size="LOCAL_UI_PAGE_SIZE"
                :item-count="localUiTemplates.length"
                @update:page="handleLocalUiPageUpdate"
              />
            </div>
          </section>

          <section v-else-if="localFontActive" class="goods-panel">
            <div class="goods-header">
              <div class="goods-summary">字体预览</div>
              <div class="goods-mode">{{ officialFonts.length }} 项</div>
            </div>
            <div v-if="fontLoadError" class="goods-empty">{{ fontLoadError }}</div>
            <div v-else-if="selectedOfficialFont" class="font-preview-stage">
              <img
                v-if="selectedOfficialFont.previewUrl"
                :src="selectedOfficialFont.previewUrl"
                :alt="selectedOfficialFont.displayName"
                class="font-preview-image"
              />
            </div>
            <div v-else class="goods-empty">请选择字体</div>
          </section>

          <section v-else class="goods-panel">
            <div class="goods-header">
              <div class="goods-summary">
                {{ favoritesOnly ? '收藏资源' : hasSearch ? '搜索结果' : '素材列表' }}：{{ total }} 项
              </div>
              <div class="goods-mode">
                <template v-if="isRecommendTopCategory">混合素材</template>
                <template v-else>
                  {{ topCategoryMode === 'music' ? '音频' : topCategoryMode === 'horizontal' ? '横版素材' : '竖版素材' }}
                </template>
              </div>
            </div>

            <div ref="goodsListStageRef" class="goods-list-stage">
              <div
                v-if="goodsList.length"
                class="goods-grid"
                :class="isRecommendTopCategory ? 'goods-grid-mixed' : `goods-grid-${topCategoryMode}`"
              >
                <button
                  v-for="item in goodsList"
                  :key="item.id"
                  type="button"
                  class="goods-card"
                  :class="[
                    { 'goods-card-active': currentGoods?.id === item.id },
                    isRecommendTopCategory ? `goods-card--${resolveGoodsCardMode(item)}` : '',
                  ]"
                  @click="handleGoodsCardClick(item)"
                >
                  <template v-if="resolveGoodsCardMode(item) === 'music'">
                    <div class="goods-music-name">{{ item.name }}</div>
                  </template>
                  <template v-else>
                    <img v-if="item.thumbUrl" :src="item.thumbUrl" :alt="item.name" class="goods-thumb" />
                    <div class="goods-name">{{ item.name }}</div>
                    <div class="goods-author">{{ item.extra || '未标注作者' }}</div>
                  </template>
                </button>
              </div>
              <div v-else class="goods-empty">{{ favoritesOnly ? '当前目录没有收藏素材' : '当前分类没有素材' }}</div>
              <div v-if="showGoodsLoading" class="goods-loading-overlay">正在加载素材...</div>
            </div>

            <div class="pager-wrap">
              <n-pagination
                :page="page"
                size="small"
                :page-size="pageSize"
                :item-count="total"
                @update:page="handlePageUpdate"
              />
            </div>
          </section>

          <section v-if="localUiActive" class="preview-panel">
            <MaterialPreviewAssets
              v-if="selectedLocalUiTemplate && selectedLocalUiGoods"
              :goods="selectedLocalUiGoods"
              :resources="[]"
              :top-category-id="Category.UI"
              :collected="false"
              :show-collect="false"
              :preview-items-override="selectedLocalUiPreviewItems"
              :size-options-override="selectedLocalUiSizeOptions"
              :apply-override="() => handleApplyLocalUi(selectedLocalUiTemplate.id)"
            />
            <div v-else class="goods-empty">请选择本地 UI 皮肤</div>
          </section>

          <section v-else-if="localFontActive" class="preview-panel">
            <div v-if="selectedOfficialFont" class="font-apply-pane">
              <div class="font-preview-title">{{ selectedOfficialFont.displayName }}</div>
              <div class="font-preview-meta">{{ selectedOfficialFont.name }}</div>
              <div class="font-preview-meta">{{ selectedOfficialFont.path }}</div>
              <button
                type="button"
                class="material-primary-button"
                :disabled="hasAppliedFont(selectedOfficialFont) || applyingFontKey === selectedOfficialFont.key"
                @click="handleApplyOfficialFont(selectedOfficialFont)"
              >
                {{ hasAppliedFont(selectedOfficialFont) ? '已使用' : applyingFontKey === selectedOfficialFont.key ? '应用中' : '应用字体' }}
              </button>
            </div>
            <div v-else class="goods-empty">请选择字体</div>
          </section>

          <section v-else class="preview-panel">
            <MaterialPreviewPane
              :goods="currentGoods"
              :resources="currentResources"
              :top-category-id="currentGoodsTopCategoryId"
              :collected="currentCollected"
              :preview-loading="showPreviewLoading"
              @toggle-collect="toggleCurrentCollection"
            />
          </section>
        </div>
      </template>
      </section>
    </div>
  </Teleport>
</template>

<style scoped>
.material-library-overlay {
  position: fixed;
  inset: 0;
  z-index: 2500;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  box-sizing: border-box;
  background: rgba(0, 0, 0, 0.18);
}

.material-modal {
  --material-font-family: "Microsoft YaHei", "PingFang SC", Arial, sans-serif;
  --material-font-size-base: 12px;
  --material-font-size-small: 11px;
  --material-font-size-title: 13px;
  --material-line-height: 1.35;
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: min(1240px, 94vw);
  height: min(760px, 84vh);
  max-height: 84vh;
  padding: 8px;
  box-sizing: border-box;
  background: var(--theme-bg-surface);
  border: 1px solid var(--theme-border);
  font-family: var(--material-font-family);
  font-size: var(--material-font-size-base);
  line-height: var(--material-line-height);
  color: var(--theme-text-primary);
  overflow: hidden;
}

.material-modal :deep(.n-tree),
.material-modal :deep(.n-pagination) {
  font-family: var(--material-font-family);
  font-size: var(--material-font-size-base);
}

.material-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding: 0 0 6px;
  border-bottom: 1px solid var(--theme-border);
  flex-shrink: 0;
}

.material-title {
  min-width: 0;
  font-size: var(--material-font-size-title);
  font-weight: 600;
  color: var(--theme-text-heading);
}

.material-header-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.material-tool-button,
.material-primary-button {
  height: 28px;
  padding: 0 12px;
  border: 1px solid var(--theme-border);
  box-sizing: border-box;
  color: var(--theme-text-primary);
  background: var(--theme-bg-panel);
  font-family: var(--material-font-family);
  font-size: var(--material-font-size-base);
  cursor: pointer;
}

.material-primary-button,
.material-tool-button:hover {
  border-color: var(--theme-accent);
  color: var(--theme-text-heading);
  background: var(--theme-bg-selected);
}

.material-primary-button:disabled {
  color: var(--theme-text-muted);
  background: var(--theme-bg-panel);
  cursor: default;
}

.material-loading {
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.login-empty {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.material-top-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 2px;
  flex-shrink: 0;
}

.top-tab {
  flex-shrink: 0;
  padding: 4px 10px;
  border: 1px solid var(--theme-border);
  background: transparent;
  color: var(--theme-text-primary);
  cursor: pointer;
  font-size: var(--material-font-size-base);
  line-height: var(--material-line-height);
}

.top-tab-favorite {
  margin-left: 6px;
}

.top-tab-active {
  border-color: var(--theme-accent);
  background: var(--theme-bg-selected);
  color: var(--theme-text-heading);
}

.material-searchbar {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  padding: 0 0 2px;
}

.material-searchbar :deep(.app-select-field) {
  width: 96px;
  max-width: 96px;
  flex: 0 0 96px;
}

.material-searchbar :deep(.material-searchbar__type) {
  width: 100%;
  height: 28px;
  padding: 0 6px;
  box-sizing: border-box;
}

.material-searchbar__input {
  flex: 1;
  min-width: 0;
  height: 28px;
  padding: 0 8px;
  box-sizing: border-box;
  border: 1px solid var(--theme-border);
  font-family: inherit;
  font-size: var(--app-font-size-md);
  line-height: 26px;
  color: var(--theme-text-primary);
  background: var(--theme-bg-panel);
  outline: none;
}

.material-searchbar__input:focus {
  border-color: var(--theme-accent);
}

.material-searchbar__button {
  height: 28px;
  padding: 0 12px;
  border: 1px solid var(--theme-border);
  box-sizing: border-box;
  flex-shrink: 0;
  color: var(--theme-text-primary);
  background: var(--theme-bg-panel);
  font-family: var(--material-font-family);
  font-size: var(--material-font-size-base);
  cursor: pointer;
}

.material-searchbar__button--primary {
  border-color: var(--theme-accent);
  color: var(--theme-text-heading);
  background: var(--theme-bg-selected);
}

.material-body {
  display: grid;
  grid-template-columns: 208px minmax(0, 1fr) 392px;
  gap: 0;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  border: 1px solid var(--theme-border);
  background: var(--theme-bg-panel);
}

.material-body--search {
  grid-template-columns: minmax(0, 1fr) 392px;
}

.sidebar,
.goods-panel,
.preview-panel {
  min-height: 0;
  background: var(--theme-bg-panel);
  border: 0;
}

.sidebar {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 6px 4px;
  box-sizing: border-box;
  border-right: 1px solid var(--theme-border);
}

.material-category-tree {
  flex: 1;
  min-height: 0;
  overflow: auto;
}

.material-category-tree :deep(.n-tree-node-content) {
  min-height: 24px;
  padding: 2px 4px;
  font-size: var(--material-font-size-base);
  line-height: var(--material-line-height);
}

.material-category-tree :deep(.n-tree-node-content--selected) {
  background: var(--theme-bg-selected);
  color: var(--theme-text-heading);
  border-left: 2px solid var(--theme-accent);
}

.goods-panel {
  display: flex;
  flex-direction: column;
  min-width: 0;
  border-right: 1px solid var(--theme-border);
}

.goods-header {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  padding: 7px 8px;
  border-bottom: 1px solid var(--theme-border);
  font-size: var(--material-font-size-base);
  color: var(--theme-text-secondary);
  flex-shrink: 0;
  background: var(--theme-bg-raised);
}

.goods-list-stage {
  position: relative;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: auto;
}

.goods-grid {
  display: grid;
  gap: 7px;
  flex: 0 0 auto;
  min-height: 0;
  overflow: visible;
  padding: 6px;
  box-sizing: border-box;
  align-content: start;
}

.goods-grid-vertical {
  grid-template-columns: repeat(auto-fill, 110px);
  grid-auto-rows: 166px;
}

.goods-grid-horizontal {
  grid-template-columns: repeat(auto-fill, 192px);
  grid-auto-rows: 106px;
}

.goods-grid-music {
  grid-template-columns: repeat(auto-fill, 192px);
  grid-auto-rows: 32px;
}

.goods-grid-mixed {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
}

.goods-card--vertical {
  width: 110px;
  height: 166px;
}

.goods-card--horizontal {
  width: 192px;
  height: 106px;
}

.goods-card--music {
  width: 192px;
  height: 32px;
  justify-content: center;
  gap: 2px;
  padding: 4px 6px;
}

.goods-card {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 0;
  min-height: 0;
  overflow: hidden;
  border: 1px solid var(--theme-border);
  background: var(--theme-bg-panel);
  color: var(--theme-text-primary);
  cursor: pointer;
  text-align: left;
}

.goods-grid-music .goods-card {
  justify-content: center;
  gap: 2px;
  padding: 4px 6px;
}

.goods-card-active {
  border-color: var(--theme-accent);
  background: var(--theme-bg-selected);
}

.goods-thumb {
  display: block;
  flex: 1;
  min-height: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  background: var(--theme-bg-secondary);
}

.goods-grid-vertical .goods-name,
.goods-grid-horizontal .goods-name,
.goods-card--vertical .goods-name,
.goods-card--horizontal .goods-name {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 20px;
  max-height: 20px;
  padding: 0 4px;
  box-sizing: border-box;
  color: #fff;
  line-height: 20px;
  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;
  background: #3f3f3f;
}

.goods-grid-vertical .goods-author,
.goods-grid-horizontal .goods-author,
.goods-card--vertical .goods-author,
.goods-card--horizontal .goods-author {
  display: none;
}

.goods-name,
.goods-music-name {
  word-break: break-word;
}

.goods-name,
.goods-music-name {
  font-size: var(--material-font-size-base);
  color: var(--theme-text-primary);
  line-height: var(--material-line-height);
  max-height: 2.7em;
  overflow: hidden;
  flex-shrink: 0;
}

.goods-music-name {
  max-height: 1.35em;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.goods-author {
  font-size: var(--material-font-size-small);
  color: var(--theme-text-secondary);
  line-height: var(--material-line-height);
  max-height: 2.7em;
  overflow: hidden;
  flex-shrink: 0;
}

.goods-empty {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 0;
  padding: 12px;
  box-sizing: border-box;
  color: var(--theme-text-muted);
  text-align: center;
}

.goods-loading-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--theme-bg-panel);
  color: var(--theme-text-secondary);
}

.pager-wrap {
  display: flex;
  justify-content: center;
  padding: 6px;
  border-top: 1px solid var(--theme-border);
  flex-shrink: 0;
  background: var(--theme-bg-raised);
}

.preview-panel {
  overflow: auto;
  min-width: 0;
}

.font-preview-stage {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 0;
  height: 100%;
  padding: 12px;
  box-sizing: border-box;
  background: var(--theme-bg-card);
}

.font-apply-pane {
  display: flex;
  flex-direction: column;
  gap: 8px;
  height: 100%;
  padding: 8px;
  box-sizing: border-box;
  background: var(--theme-bg-card);
}

.font-preview-image {
  width: min(100%, 512px);
  max-height: 100%;
  aspect-ratio: 1;
  object-fit: contain;
  background: #fff;
}

.font-preview-title {
  font-weight: 600;
  color: var(--theme-text-heading);
}

.font-preview-meta {
  color: var(--theme-text-secondary);
}

@media (max-width: 1200px) and (orientation: landscape) {
  .material-body {
    grid-template-columns: 176px minmax(0, 1fr) 320px;
  }

  .material-body--search {
    grid-template-columns: minmax(0, 1fr) 320px;
  }
}

@media (max-width: 1200px) and (orientation: portrait) {
  .material-library-overlay {
    padding: 0;
  }

  .material-modal {
    width: 100vw;
    height: 100vh;
    max-height: 100vh;
    border: 0;
  }

  .material-header {
    order: 1;
  }

  .material-searchbar {
    order: 2;
  }

  .material-loading,
  .login-empty {
    order: 3;
  }

  .material-top-tabs {
    order: 4;
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: minmax(72px, 1fr);
    gap: 0;
    min-height: 38px;
    overflow-x: auto;
    overflow-y: hidden;
    border-top: 1px solid var(--theme-border);
    background: var(--theme-bg-raised);
  }

  .top-tab {
    min-width: 0;
    height: 38px;
    padding: 0 8px;
    border: 0;
    border-right: 1px solid var(--theme-border);
    background: var(--theme-bg-raised);
    line-height: 38px;
    text-align: center;
  }

  .top-tab-favorite {
    margin-left: 0;
  }

  .top-tab-active {
    background: var(--theme-bg-panel);
  }

  .material-body {
    order: 3;
    grid-template-columns: minmax(156px, 34%) minmax(0, 1fr);
    grid-template-rows: minmax(160px, min(42%, calc(100vw * 0.5625))) minmax(0, 1fr);
    grid-template-areas:
      "preview preview"
      "sidebar goods";
  }

  .material-body--search {
    grid-template-columns: minmax(0, 1fr);
    grid-template-areas:
      "preview"
      "goods";
  }

  .sidebar {
    grid-area: sidebar;
    border-right: 1px solid var(--theme-border);
  }

  .goods-panel {
    grid-area: goods;
    border-right: 0;
  }

  .preview-panel {
    grid-area: preview;
    min-width: 0;
    min-height: 0;
    max-height: none;
    border-bottom: 1px solid var(--theme-border);
  }
}

@media (max-width: 900px) and (orientation: portrait) {
  .material-library-overlay {
    padding: 0;
  }

  .material-modal {
    width: 100vw;
    height: 100vh;
    max-height: 100vh;
  }

  .material-searchbar {
    flex-direction: row;
    align-items: center;
    gap: 4px;
  }

  .material-searchbar :deep(.app-select-field) {
    width: 56px;
    max-width: 56px;
    flex: 0 0 56px;
  }

  .material-searchbar :deep(.material-searchbar__type) {
    padding: 0 4px;
  }

  .material-searchbar__button {
    width: 52px;
    padding: 0 6px;
  }

  .material-body {
    grid-template-columns: minmax(96px, 28%) minmax(0, 1fr);
    grid-template-rows: minmax(128px, min(32%, calc(100vw * 0.5))) minmax(0, 1fr);
    grid-template-areas:
      "preview preview"
      "sidebar goods";
  }

  .material-body--search {
    grid-template-columns: minmax(0, 1fr);
    grid-template-areas:
      "preview"
      "goods";
  }
}
</style>
